import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/utils2";
import { toast } from "sonner";
import { Play, Lock, Clock, Heart, Download, FileText } from "lucide-react";

export default function EpisodeDetail() {
  const { id } = useParams();
  const [ep, setEp] = useState(null);
  const [fav, setFav] = useState(false);
  const { play } = usePlayer();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/episodes/${id}`).then((r) => setEp(r.data));
    if (user) api.get("/me/favorites").then((r) => setFav(r.data.some((e) => e.id === id)));
  }, [id, user]);

  const toggleFav = async () => {
    if (!user) return nav("/accedi");
    const r = await api.post(`/me/favorites/${id}`);
    setFav(r.data.favorited);
    toast.success(r.data.favorited ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
  };

  if (!ep) return <div className="py-32 text-center text-slate-400">Caricamento...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Link to="/episodi" className="text-sm text-slate-500 hover:text-navy">← Torna all'archivio</Link>
      <div className="mt-6 grid md:grid-cols-[280px_1fr] gap-8">
        <div className="relative">
          <img src={mediaUrl(ep.cover_url)} alt={ep.title} className="w-full rounded-2xl shadow-lg object-cover aspect-square" />
          {ep.is_premium && <span className="absolute top-3 right-3 flex items-center gap-1 bg-gold text-navy text-xs font-bold px-3 py-1.5 rounded-full"><Lock className="w-3 h-3" /> Premium</span>}
        </div>
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
            <span className="font-semibold text-navy">{ep.category}</span><span>•</span>
            <span>{fmtDate(ep.published_at)}</span><span>•</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{ep.duration}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-navy leading-tight mb-4">{ep.title}</h1>
          <p className="text-slate-600 leading-relaxed mb-6">{ep.description}</p>
          <div className="flex flex-wrap gap-2 mb-6">
            {ep.tags?.map((t) => <span key={t} className="bg-slate-100 text-slate-600 text-xs font-semibold px-3 py-1 rounded-full">#{t}</span>)}
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => play(ep)} data-testid="detail-play" className="flex items-center gap-2 bg-navy text-white rounded-full px-8 py-3 font-medium hover:bg-petrol transition-all shadow-md">
              <Play className="w-5 h-5" /> {ep.locked ? "Ascolta anteprima" : "Riproduci"}
            </button>
            <button onClick={toggleFav} data-testid="detail-favorite" className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium border transition-all ${fav ? "bg-gold/10 border-gold text-gold" : "border-slate-200 text-slate-600 hover:border-navy"}`}>
              <Heart className={`w-5 h-5 ${fav ? "fill-gold" : ""}`} /> Preferiti
            </button>
          </div>
          {ep.locked && (
            <div className="mt-6 bg-gradient-to-r from-navy to-petrol rounded-xl p-6 text-white">
              <div className="flex items-center gap-2 text-gold font-semibold mb-2"><Lock className="w-5 h-5" /> Contenuto Premium</div>
              <p className="text-slate-300 text-sm mb-4">Stai ascoltando l'anteprima. Abbonati per l'episodio completo e i contenuti esclusivi.</p>
              <Link to="/abbonamenti" data-testid="detail-subscribe" className="inline-block bg-gold text-navy rounded-full px-6 py-2.5 font-semibold hover:bg-white transition-colors">Abbonati ora</Link>
            </div>
          )}
        </div>
      </div>

      {(!ep.locked) && ep.transcript && (
        <div className="mt-12 bg-white border border-slate-100 rounded-2xl p-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl font-semibold text-navy flex items-center gap-2"><FileText className="w-5 h-5 text-gold" /> Note & Trascrizione</h2>
            <button onClick={() => { const b = new Blob([ep.transcript], { type: "text/plain" }); const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = `${ep.title}.txt`; a.click(); }} data-testid="download-transcript" className="flex items-center gap-1.5 text-sm text-navy hover:text-gold font-medium"><Download className="w-4 h-4" /> Scarica</button>
          </div>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line">{ep.transcript}</p>
        </div>
      )}
    </div>
  );
}
